import { createFingerprint } from "@quotientjs/browser-fingerprint"
import {
  AsyncStorageProvider,
  BrowserStorageProvider,
  StorageProvider,
} from "@quotientjs/browser-storage"

import { BrowserApiClient } from "./browser-api-client"

import { generateUUID } from "./uuid"

import { Audience } from "./clients/audience"
import { People } from "./clients/people"
import { Analytics } from "./clients/analytics"
import { Auth } from "./clients/auth"
import { Blog } from "./clients/blog"
import { Flow } from "./clients/flow"
import { Store } from "./clients/store"

import { DeviceIdStore } from "./stores/device-id"
import { SessionIdStore } from "./stores/session-id"
import { BrowserFingerprintStore } from "./stores/browser-fingerprint"

export interface QuotientClientOptions {
  apiKey: string
  baseUrl?: string
}

/**
 * Synchronous Quotient client for web browser environments
 * Provides analytics, authentication, and people management without async initialization
 *
 * NOTE (amiller68): this is not meant for use with the Shopify web pixel integration.
 */
export class QuotientClient {
  public auth: Auth
  public analytics: Analytics
  public audience: Audience
  /** @deprecated Use `audience.people` instead */
  public people: People
  public blog: Blog
  public flow: Flow
  public store: Store
  protected storageProvider: AsyncStorageProvider | StorageProvider
  protected apiClient: BrowserApiClient

  constructor(
    storageProvider: AsyncStorageProvider | StorageProvider,
    apiKey: string,
    baseUrl?: string,
  ) {
    this.storageProvider = storageProvider
    this.apiClient = new BrowserApiClient({
      apiKey: apiKey,
      baseUrl: baseUrl,
    })

    // Create API client
    this.apiClient.setStorageProvider(this.storageProvider)

    this.auth = new Auth(this.apiClient)
    this.analytics = new Analytics(this.apiClient)
    this.audience = new Audience(this.apiClient, this.storageProvider)
    this.people = this.audience.people
    this.blog = new Blog(this.apiClient)
    this.flow = new Flow(this.apiClient)
    this.store = new Store(this.storageProvider)
  }

  /**
   * Initialize storage with required values synchronously
   * @param browserContext The browser context required for fingerprinting
   */
  static init(options: QuotientClientOptions): QuotientClient {
    const storageProvider = new BrowserStorageProvider()

    // Initialize with device ID
    const deviceId = DeviceIdStore.get(storageProvider)
    if (!deviceId) {
      const newDeviceId = generateUUID()
      DeviceIdStore.set(storageProvider, newDeviceId)
    }

    // Initialize with session ID
    const sessionId = SessionIdStore.get(storageProvider)
    if (!sessionId) {
      const newSessionId = generateUUID()
      SessionIdStore.set(storageProvider, newSessionId)
    }

    // Initialize with browser fingerprint
    const fingerprint = BrowserFingerprintStore.get(storageProvider)
    if (!fingerprint) {
      // Generate fingerprint in the background - don't block client creation
      createFingerprint({
        context: {
          window: window,
          document: document,
          navigator: navigator,
        },
      })
        .then((newFingerprint) => {
          BrowserFingerprintStore.set(storageProvider, newFingerprint.hash)
        })
        .catch((error) => {
          console.error("Failed to generate browser fingerprint:", error)
        })
    }

    return new QuotientClient(storageProvider, options.apiKey, options.baseUrl)
  }
}
